"use client";

import { useEffect } from "react";
import { clampTransform } from "@/features/lomdaEditorV2/lib/elements";
import {
    useDocumentActions,
    useEditorStoreApi,
    useUiActions,
} from "@/features/lomdaEditorV2/store";

function isTextEntryFocused(): boolean {
    const el = document.activeElement as HTMLElement | null;
    if (!el) return false;
    const tag = el.tagName;
    return (
        tag === "INPUT" ||
        tag === "TEXTAREA" ||
        el.isContentEditable ||
        el.closest("[data-rich-text-editor]") !== null
    );
}

/**
 * Keyboard affordances for the selected canvas element: arrow-key nudge,
 * Delete to remove, Escape to deselect. Capture-phase + stopPropagation so it
 * wins over scene prev/next navigation when an element is selected.
 */
export function useCanvasKeyboard(sceneId: string | null) {
    const store = useEditorStoreApi();
    const { updateElementTransform, removeElement } = useDocumentActions();
    const { clearSelection, setActiveTextEditorId } = useUiActions();

    useEffect(() => {
        if (!sceneId) return;
        const onKey = (e: KeyboardEvent) => {
            const state = store.getState();
            const sel = state.selection;

            if (e.key === "Escape") {
                if (state.activeTextEditorId) setActiveTextEditorId(null);
                else if (sel.kind !== "none") clearSelection();
                return;
            }

            if (sel.kind !== "element") return;
            if (state.activeTextEditorId) return;
            if (isTextEntryFocused()) return;

            const scene = state.scenes.find((s) => s.id === sceneId);
            const el = scene?.elements?.find((x) => x.id === sel.elementId);
            if (!el) return;

            if (e.key === "Delete" || e.key === "Backspace") {
                e.preventDefault();
                e.stopPropagation();
                removeElement(sceneId, el.id);
                return;
            }

            const step = e.shiftKey ? 5 : 1;
            const t = { ...el.transform };
            if (e.key === "ArrowLeft") t.x -= step;
            else if (e.key === "ArrowRight") t.x += step;
            else if (e.key === "ArrowUp") t.y -= step;
            else if (e.key === "ArrowDown") t.y += step;
            else return;
            e.preventDefault();
            e.stopPropagation();
            updateElementTransform(sceneId, el.id, clampTransform(t));
        };
        window.addEventListener("keydown", onKey, true);
        return () => window.removeEventListener("keydown", onKey, true);
    }, [
        sceneId,
        store,
        updateElementTransform,
        removeElement,
        clearSelection,
        setActiveTextEditorId,
    ]);
}
